import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  ClipboardList,
  CheckCircle2,
  XCircle,
  MapPin,
  Briefcase,
  Calendar,
  User,
  Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Undo toast: show a sonner toast with an Undo button that hits the backend.
// The token has a 20 s server-side TTL. If the user does nothing the action
// stays in place; if they click Undo, the backend reverts it.
export function undoableToast(message, undoToken, onDone) {
  if (!undoToken) {
    toast.success(message);
    return;
  }
  toast.success(message, {
    duration: 6500,
    action: {
      label: 'Undo',
      onClick: async () => {
        try {
          await axios.post(`${API}/admin/undo/${undoToken}`);
          toast.success('Undone');
          if (onDone) onDone();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Cannot undo');
        }
      },
    },
  });
}

function AdminClaimRequestsPage() {
  const { user } = useAuth();
  const Layout = user?.role === 'supervisor' ? SupervisorLayout : AdminLayout;

  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, hRes] = await Promise.all([
        axios.get(`${API}/admin/claim-requests?status=pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/claim-requests`).catch(() => ({ data: [] })),
      ]);
      setPending(pRes.data || []);
      // History = everything except still-pending, most recent first
      setHistory((hRes.data || []).filter((r) => r.status !== 'pending'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const approve = async (req) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/claim-requests/${req.id}/approve`);
      const added = r.data?.added ?? 0;
      const skipped = r.data?.skipped ?? 0;
      toast.success(`Approved — ${added} day(s) assigned${skipped ? ` (${skipped} skipped)` : ''}`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const openReject = (req) => {
    setRejectTarget(req);
    setRejectReason('');
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/claim-requests/${rejectTarget.id}/reject`, {
        reason: rejectReason.trim() || null,
      });
      undoableToast('Request declined', r.data?.undo_token, fetchAll);
      setRejectTarget(null);
      setRejectReason('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const currentList = tab === 'pending' ? pending : history;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAll = () => setSelectedIds(pending.map((r) => r.id));
  const clearSelection = () => setSelectedIds([]);

  const bulkApprove = async () => {
    if (!selectedIds.length) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/claim-requests/bulk`, {
        request_ids: selectedIds,
        action: 'approve',
      });
      toast.success(r.data?.message || 'Bulk approve complete');
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setSubmitting(false);
    }
  };

  const bulkReject = async () => {
    if (!selectedIds.length) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/claim-requests/bulk`, {
        request_ids: selectedIds,
        action: 'reject',
        reason: bulkRejectReason.trim() || null,
      });
      toast.success(r.data?.message || 'Bulk decline complete');
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      setSelectedIds([]);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk decline failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <ClipboardList className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">
              CLAIM REQUESTS
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Review claim requests from employees. Approving assigns them to every requested day.
            </p>
          </div>
          {pending.length > 0 && (
            <Badge className="ml-auto bg-primary text-primary-foreground" data-testid="pending-count-badge">
              {pending.length} pending
            </Badge>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-muted/40 border border-border rounded-sm">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            {pending.length > 0 && (
              <div className="mb-3 flex items-center gap-2 p-2 bg-muted/30 border border-border rounded-sm">
                <button
                  type="button"
                  onClick={selectedIds.length === pending.length ? clearSelection : selectAll}
                  className="text-xs uppercase tracking-wider text-primary hover:underline"
                  data-testid="select-all-toggle"
                >
                  {selectedIds.length === pending.length ? 'Deselect all' : 'Select all'}
                </button>
                {selectedIds.length > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">
                      {selectedIds.length} selected
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/40 text-red-400 rounded-sm text-xs"
                        onClick={() => setBulkRejectOpen(true)}
                        disabled={submitting}
                        data-testid="bulk-reject-btn"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        Decline {selectedIds.length}
                      </Button>
                      <Button
                        size="sm"
                        className="bg-primary text-primary-foreground rounded-sm text-xs font-semibold"
                        onClick={bulkApprove}
                        disabled={submitting}
                        data-testid="bulk-approve-btn"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        Approve {selectedIds.length}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
            <RequestList list={pending} loading={loading} onApprove={approve} onReject={openReject} submitting={submitting} selectedIds={selectedIds} onToggleSelect={toggleSelect} />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RequestList list={history} loading={loading} readOnly />
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              Decline request?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {rejectTarget ? (
                <>
                  Reject <strong>{rejectTarget.employee_name}</strong>&apos;s request for{' '}
                  <strong>{rejectTarget.event_location}</strong> ({(rejectTarget.dates || []).length} day{(rejectTarget.dates || []).length !== 1 ? 's' : ''}).
                </>
              ) : null}
              <br />
              You can optionally leave a reason — it will be emailed to the employee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            className="mt-2 rounded-sm"
            rows={3}
            placeholder="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="reject-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReject}
              disabled={submitting}
              className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm"
              data-testid="confirm-reject-btn"
            >
              {submitting ? 'Declining...' : 'Yes, decline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk decline dialog */}
      <AlertDialog open={bulkRejectOpen} onOpenChange={(o) => { if (!o) setBulkRejectOpen(false); }}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow tracking-wider uppercase">
              Decline {selectedIds.length} request{selectedIds.length !== 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will reject all selected pending requests and email each employee.
              The reason (optional) is applied to every declined request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            className="mt-2 rounded-sm"
            rows={3}
            placeholder="Reason (optional) — applies to every declined request"
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            data-testid="bulk-reject-reason"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkReject}
              disabled={submitting}
              className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm"
              data-testid="confirm-bulk-reject-btn"
            >
              {submitting ? 'Declining...' : `Yes, decline ${selectedIds.length}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function RequestList({ list, loading, onApprove, onReject, submitting, readOnly, selectedIds, onToggleSelect }) {
  if (loading) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
    );
  }
  if (!list.length) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">
        {readOnly ? 'No decisions yet.' : 'No pending requests.'}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {list.map((r) => (
        <RequestCard
          key={r.id}
          r={r}
          onApprove={onApprove}
          onReject={onReject}
          submitting={submitting}
          readOnly={readOnly}
          selected={(selectedIds || []).includes(r.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}

function RequestCard({ r, onApprove, onReject, submitting, readOnly, selected, onToggleSelect }) {
  const dateRange = useMemo(() => {
    const dates = (r.dates || []).slice().sort();
    if (!dates.length) return '';
    try {
      if (dates.length === 1) return format(parseISO(dates[0]), 'EEE dd MMM yyyy');
      return `${format(parseISO(dates[0]), 'dd MMM')} → ${format(parseISO(dates[dates.length - 1]), 'dd MMM yyyy')}`;
    } catch {
      return `${dates[0]} → ${dates[dates.length - 1]}`;
    }
  }, [r.dates]);

  const createdLabel = useMemo(() => {
    try { return format(parseISO(r.created_at), 'dd MMM HH:mm'); } catch { return r.created_at; }
  }, [r.created_at]);

  const badgeCls =
    r.status === 'approved' ? 'bg-green-500/10 text-green-400 border-green-500/30'
    : r.status === 'rejected' ? 'bg-red-500/10 text-red-400 border-red-500/30'
    : r.status === 'cancelled' ? 'bg-muted/40 text-muted-foreground border-border'
    : 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30';

  return (
    <Card className={`bg-card border-border rounded-sm ${selected ? 'ring-2 ring-primary/40' : ''}`} data-testid={`claim-req-${r.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            {!readOnly && r.status === 'pending' && onToggleSelect && (
              <input
                type="checkbox"
                className="mt-1 accent-primary shrink-0"
                checked={!!selected}
                onChange={() => onToggleSelect(r.id)}
                data-testid={`select-${r.id}`}
              />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary" />
                {r.employee_name}
              </p>
              <p className="text-xs text-muted-foreground">{r.employee_email}</p>
            </div>
          </div>
          <Badge className={`text-[10px] uppercase tracking-wider border ${badgeCls}`}>
            {r.status}
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="w-3.5 h-3.5" /> {r.event_location}
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Briefcase className="w-3.5 h-3.5" /> {r.event_position}
          </div>
          <div className="flex items-center gap-1.5 text-primary font-medium">
            <Calendar className="w-3.5 h-3.5" />
            {(r.dates || []).length} day{(r.dates || []).length !== 1 ? 's' : ''} · {dateRange}
          </div>
          <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
            <Clock className="w-3 h-3" /> Requested {createdLabel}
          </div>
        </div>

        {r.rejection_reason && (
          <div className="rounded-sm bg-red-500/5 border border-red-500/30 px-3 py-2 text-xs text-red-200">
            <strong>Reason:</strong> {r.rejection_reason}
          </div>
        )}

        {!readOnly && r.status === 'pending' && (
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm text-xs uppercase"
              onClick={() => onReject(r)}
              disabled={submitting}
              data-testid={`reject-${r.id}`}
            >
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Decline
            </Button>
            <Button
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-xs uppercase font-semibold"
              onClick={() => onApprove(r)}
              disabled={submitting}
              data-testid={`approve-${r.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Approve
            </Button>
          </div>
        )}

        {readOnly && r.admin_name && (
          <div className="text-[11px] text-muted-foreground">
            Decision by <strong>{r.admin_name}</strong>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminClaimRequestsPage;
